import { memo } from 'react';
import { EntityTypes } from 'constants/entities';
import * as React from 'react';
import { useCallback } from 'react';
import { SelectItem, SelectList } from 'app/components/SelectList';
import { update } from 'core/actions/admin/data';
import { CourseNodeStatusOptions } from 'constants/nodeStatus';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { selectDefaultCourseNode } from 'core/selectors/admin/courseNode';
import { CourseNodeStatus, CourseStatus, ICourseDAO, ICourseNodeDAO } from 'types/domain/course';
import { Toggle } from 'app/components/Toggle';
import { PauseCircleIcon, PlayCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import AddButton from 'app/components/Button/AddButton';
import { selectCourse } from 'core/selectors/admin/course';
import { setModal, Modals } from 'core/slices/modals';
import { TbGhost2Filled } from 'react-icons/tb';
import { convertDate } from 'utils/convertDate';
import { CourseItemLearners, Stat } from '../../Menu/learners';
import { millisToMinutesAndSeconds, timeConversion } from 'utils/convertTime';

export const CourseNodeStatusSelector = memo(
  ({ courseNode, id, className }: { courseNode?: ICourseNodeDAO; id?: number; className?: string }) => {
    const defaultNode = useAppSelector(state => courseNode || selectDefaultCourseNode(state, id));

    const dispatch = useAppDispatch();
    const onChange = useCallback(
      val => dispatch(update({ type: EntityTypes.COURSE_NODE, entity: { id: defaultNode.id, ...val } })),
      [defaultNode?.id, dispatch],
    );

    const options: SelectItem[] = React.useMemo(() => CourseNodeStatusOptions, []);

    return (
      <SelectList
        label="Access options"
        className={`${className} relative`}
        items={options}
        value={defaultNode?.status}
        onChange={status => onChange({ status })}
      />
    );
  },
);

export const CourseNodeBasicStatus = memo(
  ({
    courseNode,
    id,
    className,
    full = true,
  }: {
    courseNode?: ICourseNodeDAO;
    id?: number;
    className?: string;
    full?: boolean;
  }) => {
    const dispatch = useAppDispatch();
    const onChange = useCallback(
      val => dispatch(update({ type: EntityTypes.COURSE_NODE, entity: { id: courseNode.id, ...val } })),
      [courseNode?.id, dispatch],
    );

    return (
      <div className={`${className} flex justify-center items-center`}>
        <Toggle
          className="flex justify-center items-center"
          value={courseNode.status !== CourseNodeStatus.DRAFT}
          onChange={status => onChange({ status: status ? CourseNodeStatus.INHERIT : CourseNodeStatus.DRAFT })}
        />
        {full && <span className="ml-2">{courseNode.status === CourseNodeStatus.DRAFT ? 'Draft' : 'Live'}</span>}
      </div>
    );
  },
);

export const CourseStatusHeader = memo(({ className }: { className?: string }) => {
  const course: ICourseDAO = useAppSelector(selectCourse);
  const dispatch = useAppDispatch();
  const statusHeader = React.useMemo(() => {
    switch (course?.status) {
      case CourseStatus.LIVE:
      case CourseStatus.PAUSED:
        return (
          <div
            className={`${className} rounded-xl flex items-center flex-shrink-0 bg-white dark:bg-howio-900 p-4 mb-5  `}
          >
            <div className="text-md text-gray-400 flex justify-between w-full mx-10">
              <div className="flex items-center">
                <span className="relative h-8 w-8 flex ">
                  <RocketLaunchIcon className="animate-pulse absolute inline-flex h-full w-full text-blue-400 opacity-75"></RocketLaunchIcon>
                  <RocketLaunchIcon className="h-8 w-8 text-blue-400"></RocketLaunchIcon>
                </span>
                <div className="text-md ml-5 flex flex-col">
                  <span className="text-gray-800 text-sm"> Workflow Started</span>
                  <span className="text-black text-md font-bold">{convertDate(course.launchDate)?.toDateString()}</span>
                </div>
              </div>

              <div className="flex justify-end items-center">
                {course.status === CourseStatus.PAUSED && (
                  <div className="text-gray-800">
                    <span>This workflow is paused. Press play to resume.</span>
                  </div>
                )}
                <div
                  className="ml-auto mr-5"
                  onClick={e => dispatch(setModal({ id: course?.id, type: Modals.PAUSE_CAMPAIGN }))}
                >
                  {course.status === CourseStatus.LIVE ? (
                    <PauseCircleIcon className="w-10 h-10 text-gray-800 cursor-pointer hover:text-white" />
                  ) : (
                    <PlayCircleIcon className="w-10 h-10 text-gray-800 cursor-pointer hover:text-white" />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case CourseStatus.DRAFT:
        return (
          <div
            className={`${className} rounded-xl flex flex-shrink-0 bg-white dark:bg-howio-900 p-4 mb-5 border border-howio-600/20`}
          >
            <div className="text-md text-gray-800 dark:text-gray-400 flex-row flex grow">
              <TbGhost2Filled className="w-10 h-10"></TbGhost2Filled>
              <div className="h-12 ml-5 pt-3">
                This workflow is in draft state: No learners can access this workflow until it is launched.
              </div>
            </div>
            <div className="h-12 mx-5 ">
              <AddButton
                className={'h-12 mx-2 w-full justify-center items-center'}
                onClick={e => dispatch(setModal({ id: course?.id, type: Modals.LAUNCH }))}
                label={'Launch Workflow'}
              />
            </div>
          </div>
        );
    }
  }, [course, dispatch]);

  return statusHeader;
});
